import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Users, CheckCircle2, TrendingUp, Sparkles, MessageSquare, Calendar } from "lucide-react";
import AdminShell from "../../components/admin/AdminShell";
import { Overline } from "../../components/Layout";
import { api } from "../../lib/api";
import { fmtDatePT, fmtTimePT } from "../../lib/time";

export default function AdminEventAnalytics() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/community/event/${id}`).then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || "Failed to load"));
  }, [id]);

  if (error) return (
    <AdminShell>
      <div className="text-sm text-muted-foreground">{error}</div>
    </AdminShell>
  );
  if (!data) return (
    <AdminShell>
      <div className="skeleton h-96" />
    </AdminShell>
  );

  const { event, totals, registrations_by_day, by_utm_source, by_utm_medium, by_utm_campaign, feedback } = data;
  const isLuma = event.source === "luma";
  const maxDay = Math.max(1, ...registrations_by_day.map((r) => r.count));

  return (
    <AdminShell testId="admin-event-analytics">
          <Link to="/admin/community" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5" data-testid="back-to-community">
            <ArrowLeft size={13} /> Community
          </Link>
          <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <Overline>Event analytics</Overline>
                {isLuma ? (
                  <span className="chip chip-purple text-[10px]">Luma</span>
                ) : (
                  <span className="chip chip-green text-[10px]">Native</span>
                )}
              </div>
              <h1 className="mt-2 text-3xl md:text-4xl font-heading font-semibold tracking-tight">{event.title}</h1>
              {event.start_at && (
                <div className="mt-1 text-sm text-muted-foreground font-mono">
                  {fmtDatePT(event.start_at, { weekday: "long", month: "long", day: "numeric", year: "numeric" })} · {fmtTimePT(event.start_at)} PT
                </div>
              )}
            </div>
            <div className="flex gap-2">
              {!isLuma && (
                <Link to={`/admin/events/${event.id}/manage`} className="btn-ghost text-sm" data-testid="manage-link">Manage event <ExternalLink size={12} /></Link>
              )}
              {isLuma && event.luma_url && (
                <a href={event.luma_url} target="_blank" rel="noreferrer" className="btn-ghost text-sm" data-testid="open-luma">Open on Luma <ExternalLink size={12} /></a>
              )}
            </div>
          </div>

          {/* KPI cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            <Kpi label="Registrations" value={totals.registrations} sub={`${totals.going} going`} icon={Users} accent="green" testId="kpi-regs" />
            <Kpi label="Check-ins" value={totals.checked_in} sub={`${totals.attendance_rate}% attendance`} icon={CheckCircle2} accent="green" testId="kpi-checkins" />
            <Kpi label="Member mix" value={`${totals.member_attendees}M / ${totals.non_member_attendees}NM`} sub={totals.capacity_used != null ? `${totals.capacity_used}% capacity` : "unlimited capacity"} icon={Users} accent="purple" testId="kpi-mix" />
            <Kpi label="Conversions" value={totals.conversions} sub={`${totals.conversion_rate}% of non-members`} icon={TrendingUp} accent="peach" testId="kpi-conversions" />
          </div>

          {/* Feedback strip */}
          <div className="glass-panel p-6 mt-6" data-testid="feedback-strip">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: "hsl(var(--vd-green) / 0.14)", color: "hsl(var(--vd-green))" }}>
                  <MessageSquare size={16} />
                </div>
                <div>
                  <Overline>Feedback</Overline>
                  <div className="text-sm mt-0.5">
                    {feedback.sent > 0 ? (
                      <>Sent to {feedback.sent} · {feedback.responded} responded ({feedback.response_rate}%)</>
                    ) : (
                      <>No feedback surveys sent yet.</>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-6">
                {feedback.nps != null && (
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground uppercase tracking-widest">NPS</div>
                    <div className="stat-value text-3xl mt-1">{feedback.nps}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">{feedback.nps_count} responses</div>
                  </div>
                )}
                {feedback.avg_rating_legacy != null && (
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground uppercase tracking-widest">Avg rating</div>
                    <div className="stat-value text-3xl mt-1">{feedback.avg_rating_legacy}</div>
                  </div>
                )}
                {!isLuma && (
                  <Link to={`/admin/events/${event.id}/manage`} className="btn-primary text-sm" data-testid="send-feedback-link">
                    <MessageSquare size={12} /> Send feedback
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Registrations by day */}
          <div className="glass-panel p-6 mt-6" data-testid="regs-by-day">
            <div className="flex items-center justify-between">
              <Overline>Registrations by day</Overline>
              <div className="text-xs text-muted-foreground font-mono">{registrations_by_day.length} day{registrations_by_day.length === 1 ? "" : "s"} of activity</div>
            </div>
            {registrations_by_day.length === 0 ? (
              <div className="mt-4 text-sm text-muted-foreground">No registrations yet.</div>
            ) : (
              <div className="mt-6 flex items-end gap-1.5 h-40">
                {registrations_by_day.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.count}`}>
                    <div className="w-full rounded-t-md transition-all" style={{ height: `${(d.count / maxDay) * 100}%`, background: "hsl(var(--vd-green))", minHeight: d.count > 0 ? "3px" : "0" }} />
                    <div className="text-[9px] text-muted-foreground font-mono truncate w-full text-center">{d.date.slice(5)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* UTM breakdown */}
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            <UtmBlock title="Top sources" rows={by_utm_source} total={totals.registrations} testId="utm-sources" />
            <UtmBlock title="Top mediums" rows={by_utm_medium} total={totals.registrations} testId="utm-mediums" />
            <UtmBlock title="Top campaigns" rows={by_utm_campaign} total={totals.registrations} testId="utm-campaigns" empty="No campaigns tagged." />
          </div>
    </AdminShell>
  );
}

function Kpi({ label, value, sub, icon: Icon, accent = "green", testId }) {
  const color = { green: "hsl(var(--vd-green))", purple: "hsl(var(--vd-purple-ink))", peach: "hsl(var(--vd-peach-ink))" }[accent];
  const bg = { green: "hsl(var(--vd-green) / 0.14)", purple: "hsl(var(--vd-purple) / 0.24)", peach: "hsl(var(--vd-peach) / 0.14)" }[accent];
  return (
    <div className="glass-panel p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        {Icon && <div className="h-8 w-8 rounded-xl flex items-center justify-center" style={{ background: bg, color }}><Icon size={14} /></div>}
      </div>
      <div className="stat-value text-3xl mt-3">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function UtmBlock({ title, rows, total, testId, empty }) {
  const nonDirect = (rows || []).filter((r) => r.key !== "direct");
  const showEmpty = nonDirect.length === 0;
  return (
    <div className="glass-panel p-5" data-testid={testId}>
      <Overline>{title}</Overline>
      {showEmpty ? (
        <div className="mt-3 text-xs text-muted-foreground">{empty || "No UTM data yet — tag share links to attribute."}</div>
      ) : (
        <div className="mt-3 space-y-2">
          {rows.slice(0, 6).map((r) => {
            const pct = total ? Math.round((r.count / total) * 100) : 0;
            return (
              <div key={r.key} className="text-sm" data-testid={`${testId}-row-${r.key}`}>
                <div className="flex items-center justify-between">
                  <span className={`truncate font-mono text-xs ${r.key === "direct" ? "text-muted-foreground" : ""}`}>{r.key}</span>
                  <span className="text-xs font-mono">{r.count} · {pct}%</span>
                </div>
                <div className="h-1 rounded-full mt-1 bg-secondary overflow-hidden">
                  <div className="h-full" style={{ width: `${pct}%`, background: r.key === "direct" ? "hsl(var(--muted-foreground) / 0.4)" : "hsl(var(--vd-green))" }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
